import { Link } from 'react-router-dom';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-500">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none text-gray-700">
            
            <p className="text-lg leading-relaxed">
              At SpeechFormation, we take your privacy seriously. This Privacy Policy explains how we collect, use, 
              disclose, and safeguard your information when you use our platform.
            </p>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              1. Information We Collect
            </h2>
            <p><strong>Information You Provide:</strong></p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Account information (name, email address, password)</li>
              <li>Profile information for child accounts (first name, age range)</li>
              <li>Payment information (processed securely through third-party payment processors)</li>
              <li>Communications you send to us</li>
            </ul>
            <p className="mt-4"><strong>Information Collected Automatically:</strong></p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Device information (browser type, operating system)</li>
              <li>Usage data (pages visited, features used, game progress)</li>
              <li>Log data (IP address, access times)</li>
            </ul>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              2. How We Use Your Information
            </h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide, maintain, and improve our platform</li>
              <li>Process transactions and send related information</li>
              <li>Send you technical notices and support messages</li>
              <li>Respond to your comments and questions</li>
              <li>Track game progress and provide personalized experiences</li>
              <li>Facilitate connections with Independent Therapists (if requested)</li>
            </ul>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              3. Children's Privacy (COPPA Compliance)
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-6">
              <p>
                SpeechFormation is committed to protecting children's privacy. Our platform is designed for use by 
                children under parental supervision. We comply with the Children's Online Privacy Protection Act (COPPA).
              </p>
            </div>
            <ul className="list-disc pl-6 space-y-2">
              <li>We require parental consent before collecting personal information from children under 13</li>
              <li>We collect only the minimum information necessary to provide our services</li>
              <li>Parents can review, delete, or refuse further collection of their child's information</li>
              <li>We do not condition a child's participation on disclosing more information than necessary</li>
              <li>Voice data used for game interaction is processed in real-time and not stored</li>
            </ul>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              4. Information Sharing
            </h2>
            <p>We do not sell your personal information. We may share information in the following circumstances:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>With Independent Therapists:</strong> Only when you explicitly request to connect with a therapist</li>
              <li><strong>Service Providers:</strong> Third parties who assist in operating our platform (payment processors, hosting services)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>

            {/* Section 5 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              5. Independent Therapist Information
            </h2>
            <p>
              Regarding Independent Therapists on our platform:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>We verify professional licenses and maintain copies on file</li>
              <li>However, we do not guarantee the accuracy, completeness, or validity of any therapist's credentials</li>
              <li>Users are encouraged to independently verify credentials before engaging services</li>
              <li>Any information shared with Independent Therapists is subject to their own privacy practices</li>
            </ul>

            {/* Section 6 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              6. Data Security
            </h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information. 
              However, no method of transmission over the Internet or electronic storage is 100% secure. We cannot 
              guarantee absolute security.
            </p>

            {/* Section 7 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              7. Your Rights and Choices
            </h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access and receive a copy of your personal information</li>
              <li>Correct inaccurate personal information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt out of marketing communications</li>
              <li>Withdraw consent where applicable</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, please contact us at privacy@speechformation.com.
            </p>

            {/* Section 8 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              8. Data Retention
            </h2>
            <p>
              We retain your personal information for as long as necessary to provide our services and fulfill the 
              purposes described in this Privacy Policy, unless a longer retention period is required by law.
            </p>

            {/* Section 9 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              9. Third-Party Links
            </h2>
            <p>
              Our platform may contain links to third-party websites or services. We are not responsible for the 
              privacy practices of these third parties. We encourage you to review their privacy policies.
            </p>

            {/* Section 10 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              10. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by 
              posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>

            {/* Section 11 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              11. Contact Us
            </h2>
            <p>
              If you have questions about this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Email:</strong> privacy@speechformation.com<br />
              <strong>Website:</strong> www.speechformation.com
            </p>

          </div>

          {/* Back Link */}
          <div className="mt-10 text-center">
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
